/**
 * Diagrams for the SL1-import Doc tab (LOCKER-382).
 *
 * ⚠ These charts and the prose in `Sl1ImportFlowDoc.tsx` are ONE documentation
 * surface and drift apart silently. The sstv2 pair did exactly that — a paragraph
 * asserting "history is never flipped" sat directly above a chart saying the same
 * thing, so neither contradicted the other and both were wrong for weeks. Change
 * them together, and change them when the SCRIPTS change.
 *
 * Mirrored from: `apps/sl2/scripts/sl1Recon{,Core,Fetch,Sink}.ts`,
 * `apps/sl2/scripts/sl1Apply{,Core}.ts`,
 * `apps/sl2/scripts/sl1Import{Artifacts,ObjectAccess}.ts`,
 * `apps/sl2/shared/sl1RetiredHuskData.ts`,
 * `apps/sl2/src/things/sl1Import/sl1ImportRepo.ts`.
 *
 * Semicolons are avoided inside node labels — a `;` ends the statement and the
 * parse error points at the NEXT line (memory: `mermaid-semicolon-breaks-labels`).
 */

/** What the two script modes read and write. The top-level orientation. */
export const SL1_TOPOLOGY_CHART = `
flowchart LR
  subgraph WEBAPP["mobilus-webapp · PostgreSQL mobilus2"]
    IC[integration_configuration<br/>tenant_id · type · archived]
    ORD[orders<br/>smart_locker_data_*<br/>date_created]
    ADDR[address<br/>locker_integration_*]
  end

  subgraph SCRIPT["apps/sl2/scripts · operator's machine"]
    SCAN["sl1Recon.ts --scan"]
    RECON["sl1Recon.ts --provider"]
    APPLY["sl1Apply.ts<br/>the only writer"]
  end

  subgraph SL2["SL2"]
    FLEET[(sl1ImportFleet<br/>one per provider)]
    JOB[(sl1ImportJob<br/>one per run)]
    RES[(reservation Things)]
    HUSK[(sl1RetiredHusk<br/>tombstones)]
  end

  SSTV1["SST v1 API<br/>GET only"]

  S3[("S3 · sl1-imports bucket<br/>report · findings · snapshot<br/>6 MB parts")]

  IC --> SCAN
  ORD --> SCAN
  SCAN -->|"snapshot, not a live query"| FLEET

  IC --> RECON
  ORD --> RECON
  ADDR --> RECON
  RES -.->|"read only · match key"| RECON
  RECON --> JOB
  RECON --> FLEET
  RECON -->|"chunked + manifest"| S3

  S3 -->|"the approved report"| APPLY
  ORD --> APPLY
  RES -.->|"read only · same classifier"| APPLY
  SSTV1 -.->|"the vendor's object for a huskless row"| APPLY
  APPLY -->|"put · original createdAt"| RES
  APPLY -->|"retire the husk it replaces"| HUSK
  APPLY --> JOB

  style APPLY fill:#fdd
  style SSTV1 fill:#efe
`

/** The per-provider recon, in the order it actually happens. */
export const SL1_RECON_CHART = `
flowchart TD
  START["sl1Recon.ts --sl2-tenant T"] --> LINK["resolveSl2ProviderLink<br/>tenant.mblsProviderId → SL1 tenant_id<br/>STOP if absent or ambiguous"]
  LINK --> JOB["create sl1ImportJob<br/>status RUNNING"]
  JOB --> ROWS["fetchSl1Rows<br/>orders ⋈ address ⋈ integration_configuration<br/>scoped to the provider's configuration ids"]
  ROWS --> LOCK["loadSl2LockerRefs<br/>lockerThingId → sstLockerId<br/>feeds lockerNotInSl2 only"]
  LOCK --> IDX["fetchSl2ReservationIndex<br/>bySstReservationId + byCodes + huskIds"]
  IDX --> CLS["classifySl1Rows · PURE"]

  CLS --> M1["matched_exact<br/>already in SL2"]
  CLS --> M2["matched_fallback<br/>code triple"]
  CLS --> M3["unmatched<br/>no counterpart"]
  CLS --> M4["ambiguous<br/>2+ candidates"]
  CLS --> M5["blocked<br/>cannot import"]

  M1 --> HK{"matchedHusk?"}
  M2 --> HK
  HK -->|"yes"| PL["replacesHusk<br/>WILL be written"]
  HK -->|"no"| NOOP["already in SL2<br/>nothing to do"]

  M3 --> PL2["unmatched<br/>WILL be written if the vendor still has it"]

  PL --> REP["buildSl1ImportReport<br/>counts · ledger · anomalies · planDigest"]
  PL2 --> REP
  NOOP --> REP
  M4 --> REP
  M5 --> REP

  REP --> LOCAL["writeReportLocal<br/>always"]
  LOCAL --> UP["uploadRun<br/>3 kinds · 6 MB parts"]
  UP --> PATCH["patch sl1ImportJob<br/>counts · manifest · verdict"]
  PATCH --> FLEET["put sl1ImportFleet<br/>all-time count re-read"]

  UP -.->|"bucket absent → degrade<br/>reportIsLocal stays true"| PATCH
`

/**
 * The ordering constraint that is not obvious and that caused a real defect.
 * Worth its own diagram because "lockers first" reads as an implementation
 * detail and is actually the correctness argument.
 */
export const SL1_MATCH_KEY_CHART = `
flowchart LR
  subgraph SL1SIDE["SL1 row"]
    A0["orders.smart_locker_data_data<br/>the cached vendor response"]
    A1["id"]
    A2["admin + reception + delivery codes"]
    A0 --> A1
    A0 --> A2
  end

  subgraph SL2SIDE["SL2 reservation Thing"]
    B0["sstReservation<br/>live v2 object"]
    B0X["preV2.sstReservation<br/>ARCHIVED v1 object"]
    B1["id"]
    B2["admin + reception + delivery codes"]
    B0 --> B1
    B0X -->|"the husk's ONLY identity"| B1
    B0 --> B2
    B0X --> B2
  end

  A1 --> EX(("id match"))
  B1 --> EX
  EX --> OUTX["matched_exact"]

  A2 --> FB(("code triple"))
  B2 --> FB
  FB --> OUTF["1 candidate → matched_fallback<br/>2+ → ambiguous<br/>0 → unmatched"]

  style B0X fill:#fdd
`

/** Where the bytes go, and what the console is allowed to read back. */
export const SL1_STORAGE_CHART = `
flowchart TD
  RUN["a recon run"] --> R["report<br/>verdict · counts · ledger · anomalies<br/>a few KB"]
  RUN --> F["findings<br/>one row per SL1 row<br/>thousands"]
  RUN --> S["snapshot<br/>RAW orders rows as read<br/>cannot be re-derived"]

  R --> CHUNK["chunkSl1Rows · 6 MB"]
  F --> CHUNK
  S --> CHUNK
  CHUNK --> PUT["PutObject<br/>sl1-import/PROVIDER/JOB/KIND/part-0001.json"]
  PUT --> MAN["manifest onto sl1ImportJob<br/>key · bytes · rows · sha256"]

  MAN --> GUARD{"decideSl1ObjectAccess"}
  BROWSER["console"] -->|"asks for a key"| GUARD
  GUARD -->|"key IS in this run's manifest"| SIGN["presigned GET · 5 min"]
  GUARD -->|"anything else"| DENY["403"]
  SIGN --> DIRECT["browser reads S3 directly<br/>6 MB would exceed API Gateway"]
  SIGN -.->|"snapshot only"| AUDIT["admin log entry"]

  style DENY fill:#fdd
  style S fill:#efe
`

/**
 * The apply, and specifically the gates that can stop it.
 *
 * Worth its own diagram because the interesting part is not the write — it is
 * everything that refuses to write. Mirrored from `planSl1Apply` +
 * `composeSl1Writes` in `sl1ApplyCore.ts` and `main` in `sl1Apply.ts`.
 */
export const SL1_APPLY_CHART = `
flowchart TD
  START["sl1Apply.ts --report R --sl2-tenant T"] --> VER{"report schemaVersion<br/>matches this build?"}
  VER -->|"no"| STOP1["STOP · re-run the recon"]
  VER -->|"yes"| REDO["re-run the SAME reads<br/>and the SAME pure classifier"]
  REDO --> DIG{"planDigest equal?"}
  DIG -->|"no"| STOP2["STOP · SL1 or SL2 moved<br/>--accept-drift overrides, loudly"]
  DIG -->|"yes"| PLAN["planSl1Apply · PURE"]

  PLAN --> SK1["skip lockerNotInSl2"]
  PLAN --> SK2["skip huskClaimedByAnotherRow"]
  PLAN --> HUSKY{"does a husk cover this row?"}

  HUSKY -->|"yes"| VH["vendor object := the husk"]
  HUSKY -->|"no"| V1{"does SST v1 still hold it?<br/>locker sweep, then per-id<br/>id must echo back"}
  V1 -->|"no"| SK3["skip vendorNoLongerHasIt"]
  V1 -->|"yes"| LOC{"same locker SL1 named?<br/>decided by sweep provenance"}
  LOC -->|"no · claimed locker was swept"| SK4["skip lockerDisagreesWithVendor"]
  LOC -->|"yes, or never swept"| DUP{"another SL1 row<br/>already claimed this SST id?"}
  DUP -->|"yes"| SK5["skip sstIdClaimedByAnotherRow"]
  DUP -->|"no"| VV["vendor object := the live v1 read"]

  VH --> COMP["composeSl1Writes"]
  VV --> COMP
  COMP --> GUARD{"status inside SL2's vocabulary?"}
  GUARD -->|"no"| REF["refused · NOT written<br/>and its husk is NOT retired"]
  GUARD -->|"yes"| W["put reservation · original createdAt"]
  W --> TOMB["put tombstone + delete husk<br/>same transaction"]

  COMP -.->|"--dry-run returns HERE<br/>after composing, so the preview<br/>is the objects the writer puts"| PREVIEW["report and stop"]

  TOMB --> TX["TransactWriteItems<br/>25 actions · 3 items each"]
  TX --> GOV["token bucket · 60 items/s<br/>halve on throttle, recover 10%/quiet min"]
  GOV --> BRK{"fan-out healthy?<br/>iteratorAge + failure queue, every 60s"}
  BRK -->|"no"| STOP3["STOP · written rows stand<br/>a re-run skips them"]
  BRK -->|"yes"| TX

  style STOP1 fill:#fdd
  style STOP3 fill:#fdd
  style STOP2 fill:#fdd
  style REF fill:#fdd
  style W fill:#efe
`
